import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
// import RegisterPage from './pages/RegisterPage'; // Not currently in use
import DashboardPage from './pages/DashboardPage';
import EmployeesPage from './pages/EmployeesPageRefactored';
import LeavesPage from './pages/LeavesPageRefactored';
import LeaveCalendarPage from './pages/LeaveCalendarPage';
import ExpensesPage from './pages/ExpensesPageRefactored';
import AssetsPage from './pages/AssetsPageRefactored';
import CRMPage from './pages/CRMPageRefactored';
import DevOpsPage from './pages/DevOpsPage';
import SettingsPage from './pages/SettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import MyPayrollPage from './pages/MyPayrollPage';
import EmployeePortalPage from './pages/EmployeePortalPage';
import HolidaysPage from './pages/HolidaysPage';
import PayrollAIPage from './pages/PayrollAIPage';
import PayrollRunPage from './pages/PayrollRunPage';
import MyTravelRequestsPage from './pages/MyTravelRequestsPage';
import NewTravelRequestPage from './pages/NewTravelRequestPage';
import TravelApprovalsPage from './pages/TravelApprovalsPageRefactored';
import TravelRequestDetailPage from './pages/TravelRequestDetailPage';
import GuestPortalPage from './pages/GuestPortalPage';
import MyTimesheetPage from './pages/MyTimesheetPage';
import TimesheetApprovalsPage from './pages/TimesheetApprovalsPageRefactored';
import NotificationsPage from './pages/NotificationsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import ErrorLogsPage from './pages/ErrorLogsPage';
import InvoicesPage from './pages/InvoicesPage';
import NewInvoicePage from './pages/NewInvoicePage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import PublicInvoicePage from './pages/PublicInvoicePage';
import MyAttendancePage from './pages/MyAttendancePage';
import AttendanceImportPage from './pages/AttendanceImportPage';
import AttendanceAdminPage from './pages/AttendanceAdminPage';
import RequisitionsPage from './pages/RequisitionsPage';
import NewRequisitionPage from './pages/NewRequisitionPage';
import CandidatesPage from './pages/CandidatesPage';
import RecruitmentDashboardPage from './pages/RecruitmentDashboardPage';
import RequisitionDetailPage from './pages/RequisitionDetailPage';
import CandidateDetailPage from './pages/CandidateDetailPage';
import OffersPage from './pages/OffersPage';
import ADSyncPage from './pages/ADSyncPage';
import RBACPage from './pages/RBACPage';
import AccountsGSTPage from './pages/AccountsGSTPage';
import LeaveEncashmentPage from './pages/LeaveEncashmentPage';
import CelebrationsPage from './pages/CelebrationsPage';
import PerformancePage from './pages/PerformancePage';
import PerformanceDisciplinePage from './pages/PerformanceDisciplinePage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import UserAccessPage from './pages/UserAccessPage';
import PettyCashPage from './pages/PettyCashPage';
import PettyCashActionPage from './pages/PettyCashActionPage';
import AssetAllocationActionPage from './pages/AssetAllocationActionPage';
import ProjectCostingPage from './pages/ProjectCostingPage';
import WhatsAppTemplatesPage from './pages/WhatsAppTemplatesPage';
import './App.css';

// Module to route mapping - used for dynamic RBAC
// Maps RBAC module IDs to the routes they control
const MODULE_ROUTE_MAP = {
  'dashboard': ['/'],
  'employees': ['/employees'],
  'ad_sync': ['/ad-sync'],
  'leaves': ['/leaves', '/leaves/encashment'],
  'expenses': ['/expenses'],
  'travel': ['/travel', '/travel/new', '/travel/:id'],
  'travel_approvals': ['/travel/approvals'],
  'assets': ['/assets'],
  'crm': ['/crm'],
  'devops': ['/devops'],
  'settings': ['/settings'],
  'users': ['/users'],
  'payroll': ['/my-payroll'],
  'payroll_runs': ['/payroll/runs', '/payroll-ai'],
  'holidays': ['/holidays'],
  'timesheets': ['/timesheet'],
  'timesheet_approvals': ['/timesheet/approvals'],
  'attendance': ['/attendance'],
  'attendance_admin': ['/attendance/admin', '/attendance/import'],
  'celebrations': ['/celebrations'],
  'performance': ['/performance'],
  'discipline': ['/discipline'],
  'purchase_orders': ['/purchase-orders'],
  'recruitment': ['/recruitment/requisitions', '/recruitment/requisitions/new', '/recruitment/requisitions/:id', '/recruitment/candidates', '/recruitment/candidates/:id', '/recruitment/analytics'],
  'recruitment_offers': ['/recruitment/offers'],
  'billing': ['/billing/invoices', '/billing/invoices/new', '/billing/invoices/:id', '/billing/payments'],
  'gst': ['/accounts/gst'],
  'petty_cash': ['/petty-cash'],
  'project_costing': ['/project-costing'],
  'audit': ['/admin/audit'],
  'error_logs': ['/admin/errors'],
  'rbac': ['/admin/rbac'],
  'user_access': ['/admin/user-access'],
  'guest_portal': ['/guest-portal'],
  'notifications': ['/notifications'],
};

// Reverse lookup function available for future use if needed
// const getModulesForRoute = (path) => { ... };

function ProtectedRoute({ children, requiredModule }) {
  const { user, loading, accessibleModules, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin has access to everything
  if (isSuperAdmin) {
    return <Layout>{children}</Layout>;
  }

  // Check module access if requiredModule is specified (string = one module; array = ANY of them)
  if (requiredModule) {
    const moduleIds = Array.isArray(accessibleModules) 
      ? accessibleModules.map(m => typeof m === 'object' ? m.id : m)
      : [];
    
    const required = Array.isArray(requiredModule) ? requiredModule : [requiredModule];
    const hasAccess = required.some((r) => moduleIds.includes(r));
    
    if (!hasAccess) {
      return (
        <Layout>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">You do not have permission to access this page.</p>
            </div>
          </div>
        </Layout>
      );
    }
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      
      {/* Dashboard - All authenticated users */}
      <Route path="/" element={<ProtectedRoute requiredModule="dashboard"><DashboardPage /></ProtectedRoute>} />
      
      {/* Employee Directory */}
      <Route path="/employees" element={<ProtectedRoute requiredModule="employees"><EmployeesPage /></ProtectedRoute>} />
      
      {/* Employee Portal */}
      <Route path="/employee-portal" element={<ProtectedRoute><EmployeePortalPage /></ProtectedRoute>} />
      
      {/* AD Sync */}
      <Route path="/ad-sync" element={<ProtectedRoute requiredModule="ad_sync"><ADSyncPage /></ProtectedRoute>} />
      
      {/* Leave Management */}
      <Route path="/leaves" element={<ProtectedRoute requiredModule="leaves"><LeavesPage /></ProtectedRoute>} />
      <Route path="/leaves/calendar" element={<ProtectedRoute requiredModule="leaves"><LeaveCalendarPage /></ProtectedRoute>} />
      <Route path="/leaves/encashment" element={<ProtectedRoute requiredModule="leaves"><LeaveEncashmentPage /></ProtectedRoute>} />
      
      {/* Celebrations (Birthday & Anniversary) */}
      <Route path="/celebrations" element={<ProtectedRoute requiredModule="celebrations"><CelebrationsPage /></ProtectedRoute>} />
      
      {/* Performance Management */}
      <Route path="/performance" element={<ProtectedRoute requiredModule="performance"><PerformancePage /></ProtectedRoute>} />
      <Route path="/discipline" element={<ProtectedRoute requiredModule="discipline"><PerformanceDisciplinePage /></ProtectedRoute>} />
      
      {/* Purchase Orders */}
      <Route path="/purchase-orders" element={<ProtectedRoute requiredModule="purchase_orders"><PurchaseOrderPage /></ProtectedRoute>} />
      
      {/* Expense Management */}
      <Route path="/expenses" element={<ProtectedRoute requiredModule="expenses"><ExpensesPage /></ProtectedRoute>} />
      
      {/* Asset Management */}
      <Route path="/assets" element={<ProtectedRoute requiredModule="assets"><AssetsPage /></ProtectedRoute>} />
      
      {/* CRM */}
      <Route path="/crm" element={<ProtectedRoute requiredModule={["crm", "meta_connect"]}><CRMPage /></ProtectedRoute>} />
      
      {/* DevOps */}
      <Route path="/devops" element={<ProtectedRoute requiredModule="devops"><DevOpsPage /></ProtectedRoute>} />
      
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute requiredModule="settings"><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/whatsapp-templates" element={<ProtectedRoute requiredModule="crm"><WhatsAppTemplatesPage /></ProtectedRoute>} />
      
      {/* User Management */}
      <Route path="/users" element={<ProtectedRoute requiredModule="users"><UserManagementPage /></ProtectedRoute>} />
      
      {/* Payroll */}
      <Route path="/my-payroll" element={<ProtectedRoute requiredModule="payroll"><MyPayrollPage /></ProtectedRoute>} />
      <Route path="/payroll-ai" element={<ProtectedRoute requiredModule="payroll_runs"><PayrollAIPage /></ProtectedRoute>} />
      <Route path="/payroll/runs" element={<ProtectedRoute requiredModule="payroll_runs"><PayrollRunPage /></ProtectedRoute>} />
      
      {/* Holidays */}
      <Route path="/holidays" element={<ProtectedRoute requiredModule="holidays"><HolidaysPage /></ProtectedRoute>} />
      
      {/* Travel */}
      <Route path="/travel" element={<ProtectedRoute requiredModule="travel"><MyTravelRequestsPage /></ProtectedRoute>} />
      <Route path="/travel/new" element={<ProtectedRoute requiredModule="travel"><NewTravelRequestPage /></ProtectedRoute>} />
      <Route path="/travel/approvals" element={<ProtectedRoute requiredModule="travel_approvals"><TravelApprovalsPage /></ProtectedRoute>} />
      <Route path="/travel/:id" element={<ProtectedRoute requiredModule="travel"><TravelRequestDetailPage /></ProtectedRoute>} />
      
      {/* Timesheet */}
      <Route path="/timesheet" element={<ProtectedRoute requiredModule="timesheets"><MyTimesheetPage /></ProtectedRoute>} />
      <Route path="/timesheet/approvals" element={<ProtectedRoute requiredModule="timesheet_approvals"><TimesheetApprovalsPage /></ProtectedRoute>} />
      
      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* Admin Pages */}
      <Route path="/admin/audit" element={<ProtectedRoute requiredModule="audit"><AuditTrailPage /></ProtectedRoute>} />
      <Route path="/admin/errors" element={<ProtectedRoute requiredModule="error_logs"><ErrorLogsPage /></ProtectedRoute>} />
      <Route path="/admin/rbac" element={<ProtectedRoute requiredModule="rbac"><RBACPage /></ProtectedRoute>} />
      
      {/* Accounts Module - GST & Taxes */}
      <Route path="/accounts/gst" element={<ProtectedRoute requiredModule="gst"><AccountsGSTPage /></ProtectedRoute>} />
      
      {/* Guest Portal */}
      <Route path="/guest-portal" element={<ProtectedRoute requiredModule="guest_portal"><GuestPortalPage /></ProtectedRoute>} />
      
      {/* Attendance Module */}
      <Route path="/attendance" element={<ProtectedRoute requiredModule="attendance"><MyAttendancePage /></ProtectedRoute>} />
      <Route path="/attendance/import" element={<ProtectedRoute requiredModule="attendance_admin"><AttendanceImportPage /></ProtectedRoute>} />
      <Route path="/attendance/admin" element={<ProtectedRoute requiredModule="attendance_admin"><AttendanceAdminPage /></ProtectedRoute>} />
      
      {/* Recruitment Module */}
      <Route path="/recruitment/requisitions" element={<ProtectedRoute requiredModule="recruitment"><RequisitionsPage /></ProtectedRoute>} />
      <Route path="/recruitment/requisitions/new" element={<ProtectedRoute requiredModule="recruitment"><NewRequisitionPage /></ProtectedRoute>} />
      <Route path="/recruitment/requisitions/:id" element={<ProtectedRoute requiredModule="recruitment"><RequisitionDetailPage /></ProtectedRoute>} />
      <Route path="/recruitment/candidates" element={<ProtectedRoute requiredModule="recruitment"><CandidatesPage /></ProtectedRoute>} />
      <Route path="/recruitment/candidates/:id" element={<ProtectedRoute requiredModule="recruitment"><CandidateDetailPage /></ProtectedRoute>} />
      <Route path="/recruitment/offers" element={<ProtectedRoute requiredModule="recruitment_offers"><OffersPage /></ProtectedRoute>} />
      <Route path="/recruitment/analytics" element={<ProtectedRoute requiredModule="recruitment"><RecruitmentDashboardPage /></ProtectedRoute>} />
      
      {/* Billing & Invoices */}
      <Route path="/billing/invoices" element={<ProtectedRoute requiredModule="billing"><InvoicesPage /></ProtectedRoute>} />
      <Route path="/billing/invoices/new" element={<ProtectedRoute requiredModule="billing"><NewInvoicePage /></ProtectedRoute>} />
      <Route path="/billing/invoices/:id" element={<ProtectedRoute requiredModule="billing"><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/billing/payments" element={<ProtectedRoute requiredModule="billing"><PaymentsPage /></ProtectedRoute>} />
      
      {/* User Access Management */}
      <Route path="/admin/user-access" element={<ProtectedRoute requiredModule="user_access"><UserAccessPage /></ProtectedRoute>} />
      
      {/* Petty Cash Management */}
      <Route path="/petty-cash" element={<ProtectedRoute requiredModule="petty_cash"><PettyCashPage /></ProtectedRoute>} />
      
      {/* Project Costing & Profitability */}
      <Route path="/project-costing" element={<ProtectedRoute requiredModule="project_costing"><ProjectCostingPage /></ProtectedRoute>} />
      
      {/* Public Petty Cash Email Action (No Auth) */}
      <Route path="/petty-cash-action" element={<PettyCashActionPage />} />
      
      {/* Public Asset Allocation Action (No Auth) */}
      <Route path="/asset-allocation-action" element={<AssetAllocationActionPage />} />
      
      {/* Public Invoice Payment (No Auth) */}
      <Route path="/pay/:token" element={<PublicInvoicePage />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import ErrorBoundary from './components/ErrorBoundary';

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
